import React, {useEffect, useRef, useState} from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {notify} from "../utils/notify";
import {useMutation} from "@apollo/react-hooks";
import {SEND_FEEDBACK} from "../database/queries/sendFeedback";
import {makeStyles} from "@material-ui/core/styles";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Grid from "@material-ui/core/Grid";
import {UPLOAD_FILE} from "../database/queries/upload";
import Chip from "@material-ui/core/Chip";

const useStyles = makeStyles(theme => ({
    grow: {
        flexGrow: 1,
    },
    title: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block',
        },
        marginRight: 10,
    },
    textboxPaper: {
        '& .MuiInputBase-root': {
            alignItems: 'flex-start',
        },
    },
    textbox: {
        alignItems: 'flex-start',
        '& .MuiInputBase-root': {
            alignItems: 'flex-start',
        },
    },
    root: {
        '& .MuiInputBase-root': {
            height: 200,
        },
    },
    noMargin: {
        marginBottom: 0,
        minHeight: 0
    },
}));

const INITIAL_VALUE = {
    projectName: '',
    priority: 'medium',
    feedbackType: 'feature',
    subject: '',
    text: '',
    uploadFiles: []
};

const FeedbackDialog = props => {
    const classes = useStyles();
    const [feedback, setFeedback] = useState(INITIAL_VALUE);
    const [feedbackError, setFeedbackError] = useState();
    const [feedbackChanged, setfeedbackChanged] = useState(false);
    const fileInputRef = useRef();

    const [sendFeedback, {error: sendFeedbackError}] = useMutation(SEND_FEEDBACK, {
        variables: {
            feedbackMessage: {
                projectName: feedback.projectName,
                priority: feedback.priority,
                feedbackType: feedback.feedbackType,
                subject: feedback.subject,
                text: feedback.text,
                uploadFiles: feedback.uploadFiles
            }
        },
    });

    const [uploadFile] = useMutation(UPLOAD_FILE);

    const handleChange = (e) => {
        const updatedFeedback = {
            ...feedback,
            [e.target.name]: e.target.value
        };
        setFeedback(updatedFeedback);
        setfeedbackChanged(feedback.projectName.length > 0 && feedback.priority.length > 0 && feedback.feedbackType.length > 0 && feedback.subject.length > 0)
    };

    function handleUploadFileChange({target: {validity, files: [file]}}) {
        if (validity.valid) uploadFile({
            variables: {file}
        }).then(uploadFileResult => {
            const f = uploadFileResult.data.uploadFile.filename;
            if (feedback.uploadFiles.indexOf(f) === -1) {
                const updatedFeedback = {
                    ...feedback,
                    uploadFiles: [...feedback.uploadFiles, f]
                };
                setFeedback(updatedFeedback);
            }
            fileInputRef.current.value = '';
        }).catch(e => {
            console.trace(e);
        })
    }

    const handleUploadFileDelete = chipIdx => () => {
        let newArr = [...feedback.uploadFiles];
        newArr.splice(chipIdx, 1);
        const updatedFeedback = {
            ...feedback,
            uploadFiles: newArr
        };
        setFeedback(updatedFeedback);
    };

    useEffect(() => {
    }, [feedback]);

    useEffect(() => {
        if (sendFeedbackError) notify('sendFeedbackError', 'error', sendFeedbackError.message);
    }, [sendFeedbackError]);

    if (sendFeedbackError) return `Error! ${sendFeedbackError.message}`;

    return (
        <div>
            <Dialog open={props.open} onClose={props.handleClose} aria-labelledby="form-dialog-title">
                <DialogTitle id="form-dialog-title">Send Feedback</DialogTitle>
                <DialogContent>
                    <Grid container className={classes.grow} spacing={2}>
                        <Grid item xs={4}>
                            <Select
                                labelid="projectName"
                                name="projectName"
                                id="projectName"
                                autoWidth
                                size="small"
                                displayEmpty
                                onChange={handleChange}
                                value={feedback.projectName}
                            >
                                <MenuItem value="" disabled>Choose project</MenuItem>
                                <MenuItem value="JunctionBox-Ext">Browser Extension</MenuItem>
                                <MenuItem value="JunctionBox-Svr">Web Interface</MenuItem>
                                <MenuItem value="JunctionBox-DB">Backend Server</MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={4}>
                            <Select
                                labelid="priority"
                                name="priority"
                                id="priority"
                                autoWidth
                                size="small"
                                displayEmpty
                                onChange={handleChange}
                                value={feedback.priority}
                            >
                                <MenuItem value="" disabled>Choose priority</MenuItem>
                                <MenuItem value="high">High</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="low">Low</MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={4}>
                            <Select
                                labelid="feedbackType"
                                name="feedbackType"
                                id="feedbackType"
                                autoWidth
                                size="small"
                                displayEmpty
                                onChange={handleChange}
                                value={feedback.feedbackType}
                            >
                                <MenuItem value="" disabled>Select Type</MenuItem>
                                <MenuItem value="bug">Bug</MenuItem>
                                <MenuItem value="feature">Feature</MenuItem>
                                <MenuItem value="message">Message</MenuItem>
                                <MenuItem value="praise">Praise</MenuItem>
                                <MenuItem value="complaint">Complaint</MenuItem>
                                <MenuItem value="typo">Typo</MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                id="subject"
                                name="subject"
                                placeholder="Subject"
                                className={classes.textbox}
                                multiline={false}
                                fullWidth={true}
                                size="small"
                                variant="outlined"
                                onChange={handleChange}
                                value={feedback.subject}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                id="text"
                                name="text"
                                placeholder="Message"
                                className={classes.textbox}
                                multiline={true}
                                fullWidth={true}
                                size="small"
                                variant="outlined"
                                onChange={handleChange}
                                value={feedback.text}
                            />
                        </Grid>
                        {feedback.uploadFiles && (
                            <Grid item xs={12}>
                                {feedback.uploadFiles.map((uploadFile, idx) => (
                                    <Chip
                                        size="small"
                                        name={`chip-${idx}`}
                                        id={`chip-${idx}`}
                                        key={idx}
                                        label={uploadFile}
                                        onDelete={handleUploadFileDelete(idx)}
                                        color="primary"
                                    />
                                ))}
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleUploadFileChange}
                                id="fileUpload"
                                name="fileUpload"
                                placeholder="Attach FIle"
                                className={classes.textbox}
                                size="small"
                            />
                        </Grid>
                    </Grid>
                    <span>{feedbackError}</span>
                </DialogContent>
                <DialogActions>
                    <Button
                        id={"feedback-cancel"}
                        onClick={props.handleClose}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        id={"feedback-proceed"}
                        disabled={!feedbackChanged}
                        onClick={() => {
                            setFeedbackError(null);
                            sendFeedback().then(function (feedbackResult) {
                                if (feedbackResult.data.sendFeedback && feedbackResult.data.sendFeedback.returnCode === 0) {
                                    notify('sendFeedback', 'info', 'Feedback sent: ' + feedbackResult.data.sendFeedback.message);
                                    setFeedback(INITIAL_VALUE);
                                    props.handleClose();
                                } else {
                                    notify('sendFeedback', 'error', 'Problem sending feedback: ' + feedbackResult.data.sendFeedback.message);
                                    setFeedbackError(feedbackResult.message);
                                }
                            })
                        }}
                        color="primary"
                    >
                        Send Feedback
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default FeedbackDialog
